import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import * as d3 from "d3";
import Color from "color";

import basketBall from "../../icons/basketBall.svg";
import soccerBall from "../../icons/soccerBall.svg";
import tennisBall from "../../icons/tennisBall.svg";
import { lineStyles } from "./utils.js";

const markerShapesBasics = {
  d: () => "",
  transform: () => "",
  points: () => "",
  href: () => ""
};

const markerShapes = {
  circle: {
    ...markerShapesBasics,
    shape: "circle"
  },
  cross: {
    ...markerShapesBasics,
    shape: "path",
    d: (markerSize) =>
      d3
        .symbol()
        .type(d3.symbolCross)
        .size(Math.pow(markerSize, 2) * 2)(),
    transform: () => `rotate(45)`
  },
  diamond: {
    ...markerShapesBasics,
    shape: "rect",
    transform: () => `rotate(45)`
  },
  hexagon: {
    ...markerShapesBasics,
    shape: "polygon",
    points: (markerSize) =>
      d3.range(0, 2 * Math.PI, (2 * Math.PI) / 6).map(function (angle) {
        return [
          Math.sin(angle) * markerSize * 1.2,
          -Math.cos(angle) * markerSize * 1.2
        ];
      })
  },
  plus: {
    ...markerShapesBasics,
    shape: "path",
    d: (markerSize) =>
      d3
        .symbol()
        .type(d3.symbolCross)
        .size(Math.pow(markerSize, 2) * 2)()
  },
  square: {
    ...markerShapesBasics,
    shape: "rect"
  },
  triangle: {
    ...markerShapesBasics,
    shape: "path",
    d: (markerSize) =>
      d3
        .symbol()
        .type(d3.symbolTriangle)
        .size(Math.pow(markerSize, 2) * 2)()
  },
  basketBall: {
    ...markerShapesBasics,
    shape: "image",
    href: () => basketBall
  },
  soccerBall: {
    ...markerShapesBasics,
    shape: "image",
    href: () => soccerBall
  },
  tennisBall: {
    ...markerShapesBasics,
    shape: "image",
    href: () => tennisBall
  }
};

// as a reducer
const getLinesFromMarkers = (markers) => {
  return markers.reduce((acc, marker) => {
    if (marker.linesFromMarker) {
      marker.linesFromMarker.forEach((line) => {
        acc.push({
          source: {
            x: marker.x,
            y: marker.y
          },
          ...line
        });
      });
    }
    return acc;
  }, []);
};

const ScatterPlot = (props) => {
  const gLinesRef = useRef();
  const gLinksRef = useRef();
  const gPlotRef = useRef();

  useEffect(() => {
    const gLines = d3.select(gLinesRef.current);
    const gLinks = d3.select(gLinksRef.current);
    const gPlot = d3.select(gPlotRef.current);

    // clear plot before re-render
    gLines.selectAll("*").remove();
    gLinks.selectAll("*").remove();
    gPlot.selectAll("*").remove();

    const lineGroups = gLines
      .selectAll("g")
      .data(getLinesFromMarkers(props.markers))
      .enter()
      .append("g");
    const linkGroups = gLinks
      .selectAll("g")
      .data(props.links)
      .enter()
      .append("g");
    const plotGroups = gPlot
      .selectAll("g")
      .data(props.markers)
      .enter()
      .append("g");

    const fillColorCheck = Color(props.fillColor).isLight() ? "#000" : "#FFF";

    plotGroups
      .append("g")
      .attr(
        "transform",
        (d) =>
          `translate(${props.x(d[props.isLandscape ? "x" : "y"])}, ${props.y(
            d[props.isLandscape ? "y" : "x"]
          )})`
      )
      .attr("class", "plot")
      .attr("id", (d) => d.id)
      .append((d) =>
        document.createElementNS(
          d3.namespaces.svg,
          markerShapes[d.markerShape || "circle"].shape
        )
      )
      .attr("d", (d) => markerShapes[d.markerShape || "circle"].d(d.markerSize))
      .attr("fill", (d) => {
        const markerColor = d.markerColor ? d.markerColor : fillColorCheck;
        return props.transparentMarkers
          ? Color(markerColor).alpha(0.75).string()
          : markerColor;
      })
      .attr("height", (d) => (d.markerSize || 10) * 2)
      .attr("href", (d) => markerShapes[d.markerShape || "circle"].href())
      .attr("points", (d) =>
        markerShapes[d.markerShape || "circle"].points(d.markerSize || 10)
      )
      .attr("r", (d) => d.markerSize || 10)
      .attr("stroke", (d) => (d.markerColor ? d.markerColor : fillColorCheck))
      .attr("stroke-width", (d) => (props.transparentMarkers ? 2 : 0))
      .attr("transform", (d) =>
        markerShapes[d.markerShape || "circle"].transform()
      )
      .attr("width", (d) => (d.markerSize || 10) * 2)
      .attr("x", (d) => -d.markerSize || -10)
      .attr("y", (d) => -d.markerSize || -10)
      // add marker initial
      .select(function (d) {
        return this.parentNode;
      })
      .append("text")
      .attr("alignment-baseline", "central")
      .attr("dy", 0)
      .attr("dx", 0)
      .attr("fill", (d) =>
        d.markerColor
          ? Color(d.markerColor).isLight()
            ? "#000"
            : "#fff"
          : Color(props.fillColor).isLight()
          ? "#fff"
          : "#000"
      )
      .attr("font-size", (d) => d.markerSize || 10)
      .attr("text-anchor", "middle")
      .text((d) => d.markerInitial || "")
      // add marker heading
      .select(function (d) {
        return this.parentNode;
      })
      .append("text")
      .attr("alignment-baseline", "central")
      .attr("dy", -20)
      .attr("dx", 0)
      .attr("fill", (d) => (Color(d.markerColor).isLight() ? "#000" : "#fff"))
      .attr("font-size", 10)
      .attr("text-anchor", "middle")
      .text((d) => d.markerHeading || "");

    // add lines
    const linePath = d3
      .line()
      .x((d) => props.x(d[props.isLandscape ? "x" : "y"]))
      .y((d) => props.y(d[props.isLandscape ? "y" : "x"]));
    const line = lineGroups.append("g").attr("class", "line-group");
    line
      .append("path")
      .attr("d", (d) => linePath([d.source, d]))
      .attr("stroke", (d) => d.strokeColor || fillColorCheck)
      .attr("stroke-width", (d) => d.strokeWidth || 2)
      .attr("stroke-dasharray", (d) => lineStyles[d.lineStyle])
      .attr("fill", "none")
      .attr("marker-end", (d) =>
        d.markerEnd
          ? `url(#${d.markerEnd}${
              Color(props.fillColor).isLight() ? "Dark" : "Light"
            })`
          : null
      )
      .select(function (d) {
        return this.parentNode;
      })
      .append("text")
      .attr("alignment-baseline", "central")
      .attr("dy", -10)
      .attr("dx", 0)
      .attr("fill", (d) => line.strokeColor || fillColorCheck)
      .attr("font-size", 10)
      .attr("text-anchor", "end")
      .attr("transform", (d) => {
        const x1 = props.x(d.source[props.isLandscape ? "x" : "y"]);
        const x2 = props.x(d[props.isLandscape ? "x" : "y"]);
        const y1 = props.y(d.source[props.isLandscape ? "y" : "x"]);
        const y2 = props.y(d[props.isLandscape ? "y" : "x"]);
        const xMid = (x1 + x2) / 2;
        const yMid = (y1 + y2) / 2;
        // todo: still not sitting right
        const angle = Math.atan2(y2 - y1, x2 - x1) * (180 / Math.PI);
        return `translate(${xMid}, ${yMid}) rotate(${angle})`;
      })
      .text((d) => (d.time ? `${d.time}'` : ""));

    // prep nodes
    const nodes = props.markers.reduce((acc, cur) => {
      acc[cur.id] = {
        [props.isLandscape ? "x" : "y"]: props.x(cur.x),
        [props.isLandscape ? "y" : "x"]: props.y(cur.y)
      };
      return acc;
    }, {});

    // add links
    const link = linkGroups
      .append("g")
      .attr("class", "link-group")
      .attr("stroke", fillColorCheck)
      .attr("stroke-opacity", (d) => 0.1 + d.value * 0.025)
      .attr("stroke-width", (d) => 1 + d.value * 0.5);
    link
      .append("line")
      .attr("x1", (d) => nodes[d.source]?.x)
      .attr("y1", (d) => nodes[d.source]?.y)
      .attr("x2", (d) => nodes[d.target]?.x)
      .attr("y2", (d) => nodes[d.target]?.y);
  }, [props]);

  return (
    <g className="scatterPlot">
      <g className="lines" ref={gLinesRef} />
      <g className="links" ref={gLinksRef} />
      <g className="plot" ref={gPlotRef} />
    </g>
  );
};

ScatterPlot.propTypes = {
  lines: PropTypes.array,
  links: PropTypes.array,
  markers: PropTypes.array,
  //
  fillColor: PropTypes.string,
  height: PropTypes.number.isRequired,
  isLandscape: PropTypes.bool,
  margin: PropTypes.shape({
    top: PropTypes.number,
    right: PropTypes.number,
    bottom: PropTypes.number,
    left: PropTypes.number
  }),
  transparentMarkers: PropTypes.bool,
  x: PropTypes.func,
  xRange: PropTypes.array,
  y: PropTypes.func,
  yRange: PropTypes.array,
  width: PropTypes.number.isRequired
};

ScatterPlot.defaultProps = {
  lines: [],
  links: [],
  markers: [],
  //
  fillColor: null,
  height: 400,
  isLandscape: true,
  margin: {
    top: 30,
    right: 30,
    bottom: 30,
    left: 30
  },
  transparentMarkers: false,
  x: d3.scaleLinear().domain([0, 100]),
  xRange: [0, 100],
  y: d3.scaleLinear().domain([0, 100]),
  yRange: [100, 0],
  width: 600
};

export default ScatterPlot;
